import FORM_AUDIENCE_TYPE from '../../../../constants/FormAudienceType';
import FORM_ASSIGNMENT_TYPE from '../../../../constants/FormAssignmentType';
import {FORM_ACCESS_LEVELS} from '../../../../constants/formAccessLevels';
import {GROUP_MEMBERS_TYPE} from '../../../../constants/GroupMembersType';
import {PARENT_AUDIENCE_TYPE} from '../../../../constants/ParentAudienceType';
import FILTER_OPERATIONS from '../../../../constants/filterOperations';

export const isFormLinkedToStudents = (form) => !!(form?.linkToStudents ?? form?.LinkToStudents);

export const isLinkedEmployeeForm = (form) =>
    isFormLinkedToStudents(form) && Number(form?.audience) === FORM_AUDIENCE_TYPE.employees;

const isEnquiryAudienceForm = (form) =>
    Number(form?.audience) === FORM_AUDIENCE_TYPE.enquiries ||
    Number(form?.parentAudienceType) === PARENT_AUDIENCE_TYPE.enquiry;

const isExternalUsersStudentAssignmentForm = (form) =>
    Number(form?.audience) === FORM_AUDIENCE_TYPE.externalUsers &&
    Number(form?.assignmentType) === FORM_ASSIGNMENT_TYPE.students;

const isExternalUsersEmployeeAssignmentForm = (form) =>
    Number(form?.audience) === FORM_AUDIENCE_TYPE.externalUsers &&
    Number(form?.assignmentType) === FORM_ASSIGNMENT_TYPE.employees;

const isExportableExternalUsersAssignmentForm = (form) =>
    isExternalUsersStudentAssignmentForm(form) ||
    isExternalUsersEmployeeAssignmentForm(form);

export const shouldShowExportMembersTypeChooser = (form) => isLinkedEmployeeForm(form);

export const canExportFormResponsesToGroup = (form) => Boolean(form) &&
    (!isFormLinkedToStudents(form) || isLinkedEmployeeForm(form) || isExportableExternalUsersAssignmentForm(form)) &&
    (Number(form?.audience) !== FORM_AUDIENCE_TYPE.externalUsers || isExportableExternalUsersAssignmentForm(form)) &&
    form?.template?.type !== 'shareable' &&
    !form?.isCommunication;

export const getDefaultExportMembersType = (form) => {
    if (Number(form?.audience) === FORM_AUDIENCE_TYPE.employees ||
        isExternalUsersEmployeeAssignmentForm(form)) {
        return GROUP_MEMBERS_TYPE.employee;
    }

    if (Number(form?.audience) === FORM_AUDIENCE_TYPE.students) {
        return GROUP_MEMBERS_TYPE.students;
    }

    if (Number(form?.audience) === FORM_AUDIENCE_TYPE.parentsForChildCompletion) {
        return GROUP_MEMBERS_TYPE.students;
    }

    if (isEnquiryAudienceForm(form)) {
        return GROUP_MEMBERS_TYPE.students;
    }

    if (Number(form?.accessLevel) === FORM_ACCESS_LEVELS.public) {
        return GROUP_MEMBERS_TYPE.students;
    }

    if (isExternalUsersStudentAssignmentForm(form)) {
        return GROUP_MEMBERS_TYPE.students;
    }

    return GROUP_MEMBERS_TYPE.none;
};

const flattenGroupFilterTree = (filters = [], membersType) => {
    const roots = [];
    const stack = [];

    filters.forEach((filter) => {
        const node = {...filter, children: []};

        while (stack.length > 0 && stack[stack.length - 1].level >= filter.level) {
            stack.pop();
        }

        if (stack.length > 0) {
            stack[stack.length - 1].children.push(node);
        } else {
            roots.push(node);
        }

        stack.push(node);
    });

    const flatten = (nodes) => nodes.flatMap((node) => {
        const flattenedChildren = flatten(node.children);
        const matchesSelectedMembersType = node.membersType == null || node.membersType === membersType;

        if (!matchesSelectedMembersType && flattenedChildren.length === 0) {
            return [];
        }

        const {children, ...rest} = node;
        return [rest, ...flattenedChildren];
    });

    return flatten(roots);
};

export const filterGroupModulesByMembersType = (modules = [], membersType) =>
    modules.reduce((result, module) => {
        const filters = flattenGroupFilterTree(module.filters ?? [], membersType);

        if (filters.length > 0) {
            result.push({
                ...module,
                filters
            });
        }

        return result;
    }, []);

const mapQuestionAnswersToResponseFilters = (questionAnswers = [], resolveComponentPathInfo) =>
    questionAnswers.map(({
                             relatedComponentId,
                             targetStateId
                         }) => {
        const {
            componentId,
            jsonParentPaths
        } = resolveComponentPathInfo(relatedComponentId);

        return {
            propertyName: componentId,
            value: targetStateId,
            jsonParentPaths,
            operation: FILTER_OPERATIONS.EQUALS,
        };
    });

export const buildCreateGroupFromResponsesPayload = ({
    formId,
    groups,
    parentGroupFilterId,
    isGroupDynamic,
    membersType,
    resolveComponentPathInfo
}) => ({
    formId,
    parentGroupFilterId: parentGroupFilterId && parentGroupFilterId !== '' ? parentGroupFilterId : null,
    isDynamic: isGroupDynamic,
    ...(membersType != null ? {membersType} : {}),
    groups: groups.map(({
                            groupName,
                            formCategoryId,
                            questionAnswers
                        }) => ({
        groupName,
        formCategoryId,
        responseFilters: mapQuestionAnswersToResponseFilters(questionAnswers, resolveComponentPathInfo)
    })),
});

export const buildExtendGroupPayload = ({
    formId,
    operations,
    membersType,
    resolveComponentPathInfo
}) => ({
    formId,
    ...(membersType != null ? {membersType} : {}),
    operations: operations.map(({
                                    existingGroupFilterId,
                                    questionAnswers
                                }) => ({
        groupFilterId: existingGroupFilterId,
        responseFilters: mapQuestionAnswersToResponseFilters(questionAnswers, resolveComponentPathInfo)
    })),
});
